import {
	CargoUnitLabels,
	CargoUnitSingularLabels,
	CargoUnitTypes,
	Currencies,
	FreightRateType,
	HIIFieldTypes,
	InvoiceTemplates,
	PnlGroups,
} from '@shared/utils/constants';
import TemplateItem from '@shared/TemplateItem';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import {
	calculateTotal,
	round,
} from '@shared/utils/math';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatQuantity } from '@shared/utils/string';
import calculateTotalFreight from '@shared/utils/calculateTotalFreight';
import { toMoment } from '@shared/utils/date';
import type { HIIFreightType } from '@api/models/hii-freight';
import type HireInvoiceItemModel from '@api/models/hire-invoice-item';
import type CargoPort from '@api/models/cargo-port';
import type { CargoSplitItemProps } from '@api/models/cargo-split-item';
import HireInvoiceItem, { HIIConstructorParams } from './HireInvoiceItem';

type CargoPortWithSplitItems = CargoPort & {
	CargoSplitItems?: Array<CargoSplitItemProps>;
};

class HIIFreight extends HireInvoiceItem {
	static get itemType() {
		return 'freight';
	}

	cargoId: number;
	quantity: number;
	cargoType: string;
	freightRate: number;
	freightType: Values<typeof FreightRateType>;
	charterer: number | null;
	note: string;
	unit: Values<typeof CargoUnitTypes>;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	percentage: number;
	maxPercentage: number;
	addressCommission: number;
	brokerCommission: number;
	cargoPorts: CargoPortWithSplitItems[] | null;
	cpDate: Date | null;
	blNumber: string;
	blQuantity: number | null;

	constructor(
		id: number,
		isOriginal: boolean,
		params: HIIConstructorParams & {
			cargoId: number;
			quantity: number;
			cargoType: string;
			freightRate: number;
			freightType: Values<typeof FreightRateType>;
			charterer: number | null;
			note: string;
			unit: Values<typeof CargoUnitTypes>;
			currency: Values<typeof Currencies>;
			exchangeRate: number;
			percentage: number;
			maxPercentage: number;
			addressCommission: number;
			brokerCommission: number;
			cargoPorts: CargoPortWithSplitItems[] | null;
			cpDate: Date | null;
			blNumber: string;
			blQuantity?: number | null;
		},
	) {
		super(id, isOriginal, { fixtureCurrency: params?.fixtureCurrency });
		this.cargoId = params.cargoId;
		this.quantity = params.quantity;
		this.cargoType = params.cargoType;
		this.freightRate = params.freightRate;
		this.freightType = params.freightType;
		this.charterer = params.charterer;
		this.note = params.note;
		this.unit = params.unit;
		this.currency = params.currency;
		this.exchangeRate = params.exchangeRate;
		this.addressCommission = params.addressCommission;
		this.brokerCommission = params.brokerCommission;
		this.cargoPorts = params.cargoPorts;
		this.cpDate = params.cpDate;
		this.blNumber = params.blNumber;
		this.blQuantity = params.blQuantity ?? null;

		this.percentage = params.percentage;
		this.maxPercentage = params.maxPercentage ?? params.percentage;

		this._showTotal = true;
		this.addManually = true;

		this.itemTypeLabel = 'Freight';
		this.pnlGroup = PnlGroups.FREIGHT;
		this.templateSection = TemplateItem.Sections.FREIGHT;

		this._fields = {
			quantity: {
				type: HIIFieldTypes.NUMBER,
				label: 'Quantity',
			},
			freightRate: {
				type: HIIFieldTypes.CURRENCY,
				label: 'Freight Rate',
			},
		};
	}

	getCommissionBase(convertCurrency?: boolean) {
		return calculateTotalFreight(this, this.fixtureCurrency, convertCurrency);
	}

	_calculateTotal(_invoiceItems?: HireInvoiceItem[], convertCurrency?: boolean) {
		return round(this.getCommissionBase(convertCurrency) * (this.percentage / 100), 2);
	}

	getDescription() {
		return `${formatQuantity((this.quantity), this.unit)} ${this.cargoType} at 
		${formatCurrency(this.freightRate ?? 0, this.currency)}/${this.unit}`;
	}

	static async _getClassParams(model: HIIFreightType, _parentModel: typeof HireInvoiceItemModel) {
		const cargo = await model.getCargo();

		const cargoPorts = await cargo.getCargoPorts({
			include: [{ association: 'CargoSplitItems' }],
		});

		return {
			cargoId: cargo.id,
			cargoType: cargo.type,
			freightRate: model.freightRate,
			freightType: model.freightType ?? cargo.freightType,
			charterer: cargo.charterer,
			unit: cargo.unit,
			currency: model.currency ?? cargo.currency,
			exchangeRate: model.exchangeRate ?? cargo.exchangeRate,
			quantity: model.quantity,
			percentage: model.percentage,
			cpDate: cargo.cpDate,
			cargoPorts,
			blQuantity: model.blQuantity,
		};
	}

	async saveModel(model: HIIFreightType) {
		await super.saveModel(model);

		model.cargoId = this.cargoId;
		model.percentage = this.percentage;
		model.quantity = this.quantity;
		model.freightRate = this.freightRate;
		model.currency = this.currency;
		model.exchangeRate = this.exchangeRate;
		model.freightType = this.freightType;
		model.blQuantity = this.blQuantity;

		await model.save();
	}

	_getTemplateItemParams() {
		const freightRateWithUnit = (
			this.freightType === FreightRateType.PER_UNIT ?
				`${formatCurrency(this.freightRate, this.currency)} / ${CargoUnitSingularLabels[this.unit]}` :
				`${formatCurrency(this.freightRate, this.currency)}`
		);

		const getSplitTableRow = (splitItems: Array<CargoSplitItemProps>) => {
			const rows = [
				['', 'BL Cargo', `BL Quantity (${this.unit})`, 'BL Date', 'BL Number', 'BL Description'],
				[
					'Total',
					this.cargoType,
					formatNumber(calculateTotal(splitItems, (i) => i.quantity ?? 0)),
					'',
					'',
					'',
				],
				...splitItems.map((item, index) => [
					`BL ${index + 1}`,
					item.cargoText || this.cargoType,
					item.quantity != null ? formatNumber(item.quantity) : '',
					item.date ? toMoment(item.date).format('DD-MM-YYYY') : '',
					item.blNumber ?? '',
					item.description ?? '',
				]),
			];

			return [{
				className: 'blSplitTable',
				content: rows.map((row) => row.map((content) => ({ content }))),
			}];
		};

		const getPortBlQuantity = (cp: CargoPortWithSplitItems, actionPorts: CargoPortWithSplitItems[]) => {
			const lineTotal = this.blQuantity ?? this.quantity;
			const liveTotal = calculateTotal(actionPorts, (p) => p.blQuantity ?? 0);

			if (cp.blQuantity == null) {
				return null;
			}

			return liveTotal > 0 ? round((cp.blQuantity / liveTotal) * lineTotal, 3) : cp.blQuantity;
		};

		const formatPorts = (action: string) => (Array.isArray(this.cargoPorts) ?
			this.cargoPorts
				.filter((cp) => cp.portAndActionKey?.toLowerCase().includes(action))
				.flatMap((cp, _index, actionPorts) => {
					const dateStr = cp.blDate ? toMoment(cp.blDate).format('DD-MM-YYYY') : '';
					const portBlQuantity = getPortBlQuantity(cp, actionPorts);
					const qtyStr = portBlQuantity != null ?
						`${formatQuantity(portBlQuantity, this.unit)}` :
						'';
					const details = [dateStr && `B/L date: ${dateStr}`, qtyStr && `B/L quantity: ${qtyStr}`]
						.filter(Boolean)
						.join(', ');

					const portRow = [`${cp.port.name}${details ? ` (${details})` : ''}`];
					const splitItems = cp.CargoSplitItems ?? [];

					return splitItems.length > 0 ?
						[portRow, getSplitTableRow(splitItems)] :
						[portRow];
				}) :
			[]);

		const loadingRows = formatPorts('loading');
		const dischargingRows = formatPorts('discharging');
		const formattedCpDate = this.cpDate ? toMoment(this.cpDate).format('DD-MM-YYYY') : '';
		const classicTitle = this.withChangeSuffix(
			`${formatQuantity(this.quantity, this.unit)} ${this.cargoType} 
							@ ${freightRateWithUnit} (${this.percentage}%)`,
		);
		const neoTitle = this.withChangeSuffix(`${this.cargoType} (${this.percentage}%)`);

		return {
			[InvoiceTemplates.CLASSIC]: {
				columns: [
					[
						[
							classicTitle,
						],

						['Loading:'],
						...loadingRows,

						['Discharging:'],
						...dischargingRows,

						[`CP date: ${formattedCpDate}`],

						...(this.blNumber ? [[`BL number: ${this.blNumber}`]] : []),
					],
				],
				children: [
					[this.note],
				],
			},
			[InvoiceTemplates.NEO]: {
				columns: [
					[
						[neoTitle],

						['Loading:'],
						...loadingRows,

						['Discharging:'],
						...dischargingRows,

						[`CP date: ${formattedCpDate}`],

						...(this.blNumber ? [[`BL number: ${this.blNumber}`]] : []),
					],
					formatNumber(round(this.quantity, 3), { separateThousands: true }),
					CargoUnitLabels[this.unit],
					`${formatCurrency(this.freightRate, this.currency, { hideSymbol: true })}`,
				],
				children: this.note != null ? [
					[{
						content: this.note,
						small: true,
						italic: true,
					}],
				] : undefined,
			},
		};
	}
}

HireInvoiceItem.addItemClass(HIIFreight);

export default HIIFreight;
